/**
 * Whether the editor's changes have reached the server — the pending-changes
 * indicator of the offline edit queue (`api/rundownEditor.ts`):
 *
 *   all saved      nothing unanswered
 *   saving…        edits sent (or about to be) and not yet answered
 *   N offline      the server is unreachable; N edits are held, in order, and
 *                  are sent when it answers again
 *
 * Presentational: the session's counts in, text out (a polite live region, so
 * a screen reader hears the editor go offline and come back).
 */
import { CheckIcon, CloudOffIcon, LoaderIcon } from 'lucide-react';
import { cn } from '../../lib/utils.js';

export type SaveStatusProps = {
  /** Edits applied here and not yet answered by the server. */
  readonly pending: number;
  /** The last send failed without an answer; the queue is waiting to retry. */
  readonly offline: boolean;
};

export function saveStatusText(pending: number, offline: boolean): string {
  if (pending === 0) return 'All changes saved';
  if (offline) return `${String(pending)} ${pending === 1 ? 'edit' : 'edits'} offline — will send when the server is back`;
  return 'Saving…';
}

export function SaveStatus({ pending, offline }: SaveStatusProps) {
  const state = pending === 0 ? 'saved' : offline ? 'offline' : 'saving';
  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="save-status"
      data-state={state}
      data-pending={pending}
      className={cn('flex items-center gap-1 rounded-md px-2 py-0.5 text-xs', state === 'offline' ? 'bg-warning text-warning-foreground' : 'text-muted-foreground')}
    >
      {state === 'saved' ? <CheckIcon className="size-3.5" aria-hidden="true" /> : null}
      {state === 'saving' ? <LoaderIcon className="size-3.5 animate-spin" aria-hidden="true" /> : null}
      {state === 'offline' ? <CloudOffIcon className="size-3.5" aria-hidden="true" /> : null}
      {saveStatusText(pending, offline)}
    </span>
  );
}
